import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { getCookie } from "typescript-cookie";
import { EntEconomicActivityTypes } from "../../../../../models/handBooks/economicActivityTypes";
import { useAddEconomicActivityTypesMutation, useDeleteEconomicActivityTypesMutation, useEditEconomicActivityTypesMutation } from "../../../../../redux/api/handBooksApi/economicActivityTypesApi";

const useEconomicActivityTypes = (modal: any, refresh?: () => void) => {
    const { t } = useTranslation()
    const { confirm } = modal;
    const [addEconomicActivityTypes] = useAddEconomicActivityTypesMutation();
    const [deleteEconomicActivityTypes] = useDeleteEconomicActivityTypesMutation();
    const [editEconomicActivityTypes] = useEditEconomicActivityTypesMutation();

    async function onAddEconomicActivityTypes(newEconomicActivityTypes: EntEconomicActivityTypes): Promise<boolean> {
        try {
            let status: boolean = false;
            await addEconomicActivityTypes(newEconomicActivityTypes).then((response: any) => {
                if (response.error) {
                    const isKyrgyz = getCookie('i18next') === 'ky';
                    const lang = isKyrgyz ? 'kg' : 'ru';
                    const res = response.error.data?.res;
                    const errorMessages = Array.isArray(res)
                        ? res.map((error: any) => error[lang] || error.ru).join('\n')
                        : response.error.data?.msg || t('unknownError'); 
                    throw new Error(errorMessages);
                }
                if (response.data.success) {
                    status = true;
                    message.success(t('msg.successAdd'));
                    if (refresh) refresh();
                }
            }).catch((error: any) => {
                modal.error({ title: t('msg.error'), content: String(error.message) });
            });
            return status;
        } catch (error: any) {
            modal.error({ title: t('msg.error'), content: String(error.message) });
            return false;
        }
    }

    async function onDeleteEconomicActivityTypes(economicActivityTypes: EntEconomicActivityTypes) {
        try {
            let status: boolean = false;
            confirm({
                centered: true,
                title: t('msg.confirmDeleteText'),
                okText: t('general.yes'),
                cancelText: t('general.no'),
                onOk() {
                    deleteEconomicActivityTypes(economicActivityTypes.id as number).then((response: any) => {
                        if (response.error !== undefined && response.error.status === 400) {
                            status = false;
                            throw new Error(response.error.data.message);
                        }
                        if (response.data.success) {
                            status = true;
                            Modal.success({ title: t('msg.successDelete') });
                            if (refresh) refresh();
                        }
                    }).catch((error: any) => {
                        Modal.error({ title: t('msg.error'), content: String(error.message) });
                    });
                }
            })
            return status;
        } catch (error: any) {
            Modal.error({ title: t("msg.error"), content: String(error.message) });
            return false;
        }
    };

    async function onEditEconomicActivityTypes(editedEconomicActivityTypes: EntEconomicActivityTypes): Promise<boolean> {
        try {
            let status: boolean = false;
            await editEconomicActivityTypes(editedEconomicActivityTypes).then((response: any) => {
                if (response.error !== undefined && response.error.status === 400) {
                    throw new Error(response.error.data.message);
                }
                if (response.data.success) {
                    status = true;
                    Modal.success({ title: t('msg.successEdited') });
                    if (refresh) refresh();
                }
            }).catch((error: any) => {
                Modal.error({ title: t('msg.error'), content: String(error.message) });
            });
            return status;
        } catch (error: any) {
            Modal.error({ title: t("msg.error"), content: String(error.message) });
            return false;
        }
    };

    return { onAddEconomicActivityTypes, onDeleteEconomicActivityTypes, onEditEconomicActivityTypes };
}

export { useEconomicActivityTypes };
